/**
 * MapMarker — one camera pinned on a floor plan.
 *
 * The look follows the plan mock (mock-trinetra-ai-module/images/floorPlan2.png): a thick
 * white ring around a coloured disc with a small light core, plus a field-of-view cone when
 * the camera has been given a facing. Alerting pins add an expanding ripple in the severity
 * colour and blink at the severity tempo, so a plan tells you where at a glance and the
 * panel beside it tells you what.
 *
 * Colour is the SAME language as the hexagon board (ALERT_FX / HexColorState): red / amber /
 * blue for critical / warning / info, slate-navy online, dim slate offline. Two dots below
 * the pin mirror the hexagon's Live and Recording dots.
 *
 * Every pin carries its FACING: a notch on the rim pointing where the camera looks, plus a
 * field-of-view cone spread by `fov`. Both are drawn always — a camera's direction is a
 * property of the install, not an optional decoration, and a plan that only shows dots
 * tells you where the hardware is but not what it can see.
 *
 * Positioned in CONTENT space (left/top = the fraction × the image's natural size) and
 * COUNTER-SCALED by the canvas zoom, so pins keep a constant on-screen size the way a
 * mapping UI's do — zooming in reveals more plan detail, not bigger blobs.
 */
import { useId, type CSSProperties } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { ALERT_FX, type HexColorState } from '../hexagon/types';
import type { HexCounter } from '../../api/contracts';

/** Pin diameter in SCREEN pixels (constant across zoom levels). */
export const MARKER_SIZE = 30;
/** How far the field-of-view cone reaches, as a multiple of the pin diameter. */
const CONE_REACH = 1.9;
/** Distance from the pin's centre to the rotation knob, in screen pixels. */
const HANDLE_ORBIT = MARKER_SIZE * 1.15;

/**
 * SVG path for a field-of-view wedge of `fov` degrees, centred on straight-up (the whole
 * marker is then rotated to the camera's bearing). Drawn in a 100×100 box with the apex at
 * the centre. Above ~300° the wedge is effectively a full circle, so draw one — an arc
 * that wraps past a full turn renders as nothing.
 */
function conePath(fov: number, radius: number): string {
  const spread = Math.max(1, Math.min(360, fov));
  if (spread >= 300) return `M50,50 m-${radius},0 a${radius},${radius} 0 1,0 ${radius * 2},0 a${radius},${radius} 0 1,0 -${radius * 2},0`;
  const half = (spread / 2) * (Math.PI / 180);
  // -90° puts 0° at the top (north); x = sin, y = -cos gives the same convention.
  const x1 = 50 - Math.sin(half) * radius;
  const y1 = 50 - Math.cos(half) * radius;
  const x2 = 50 + Math.sin(half) * radius;
  const y2 = 50 - Math.cos(half) * radius;
  const largeArc = spread > 180 ? 1 : 0;
  return `M50,50 L${x1.toFixed(2)},${y1.toFixed(2)} A${radius},${radius} 0 ${largeArc},1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`;
}

/** Non-alerting fills. Navy for a healthy camera, washed slate for one that's down. */
const BASE_FILL: Record<'grey' | 'offline', { fill: string; core: string }> = {
  grey: { fill: '#2c3e50', core: '#ffffff' },
  offline: { fill: '#94a3b8', core: '#e2e8f0' },
};

// Injected once — a shared component can't rely on either app's Tailwind config carrying
// custom keyframes. Versioned: bump when the keyframes CHANGE, or a stale style element
// would satisfy the guard and the new animation names would never register.
const MARKER_FX_KEYFRAMES_ID = 'map-marker-fx-kf-v1';
if (typeof document !== 'undefined' && !document.getElementById(MARKER_FX_KEYFRAMES_ID)) {
  const el = document.createElement('style');
  el.id = MARKER_FX_KEYFRAMES_ID;
  el.textContent =
    // The ripple: a ring that grows out of the pin and fades, like the mock's halos.
    '@keyframes mapPinRipple{0%{transform:scale(1);opacity:.55}70%{opacity:0}100%{transform:scale(3.2);opacity:0}}' +
    // The pin itself breathes so an alerting camera reads as alerting even mid-ripple.
    '@keyframes mapPinPulse{0%,100%{opacity:1}50%{opacity:.6}}';
  document.head.appendChild(el);
}

export interface MapMarkerProps {
  name: string;
  colorState: HexColorState;
  counter?: HexCounter;
  live: boolean;
  recording: boolean;
  /** Fractions of the image (0..1). */
  x: number;
  y: number;
  /** Natural image size — turns the fractions into content-space pixels. */
  imageWidth: number;
  imageHeight: number;
  /** Current canvas zoom; the pin counter-scales by 1/scale. */
  scale: number;
  /** Where the camera POINTS: degrees clockwise from north. Always drawn. */
  rotation?: number;
  /** How WIDE it sees, in degrees — the cone's spread. */
  fov?: number;
  selected?: boolean;
  /** Edit mode: the pin can be dragged and removed. */
  editable?: boolean;
  /** True while THIS pin is being dragged (suppresses the hover chrome). */
  dragging?: boolean;
  /** Edit mode + selected: show the knob that spins the camera to a new bearing. */
  showRotateHandle?: boolean;
  /** True while THIS pin is being rotated. */
  rotating?: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
  onRotatePointerDown?: (e: React.PointerEvent) => void;
  onClick?: () => void;
  /** Opens the pin's install-detail dialog (height, facing, tilt, note). */
  onDoubleClick?: () => void;
  onRemove?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}

/** A minimized status dot — green = running, red = stopped, as on the hexagon. */
function StatusDot({ on, title }: { on: boolean; title: string }) {
  return (
    <span
      title={title}
      className={`h-[5px] w-[5px] rounded-full ring-1 ring-black/30 ${on ? 'bg-emerald-400' : 'bg-red-500'}`}
    />
  );
}

export default function MapMarker({
  name,
  colorState,
  counter,
  live,
  recording,
  x,
  y,
  imageWidth,
  imageHeight,
  scale,
  rotation = 0,
  fov = 90,
  selected,
  editable,
  dragging,
  showRotateHandle,
  rotating,
  onPointerDown,
  onRotatePointerDown,
  onClick,
  onDoubleClick,
  onRemove,
  onHoverStart,
  onHoverEnd,
}: MapMarkerProps) {
  // Gradient ids must be unique per pin — a duplicate would make every cone on the plan
  // adopt whichever marker mounted first.
  const gradientId = useId().replace(/:/g, '');
  const fx = ALERT_FX[colorState];
  const alerting = !!fx;
  const fill = fx ? fx.core : BASE_FILL[colorState === 'offline' ? 'offline' : 'grey'].fill;
  const core = fx ? '#ffffff' : BASE_FILL[colorState === 'offline' ? 'offline' : 'grey'].core;
  const total = counter && counter.total > 0 ? counter.total : null;

  return (
    <div
      // `data-hex` is what HexCanvas checks to let a press through as a click/drag on the
      // marker instead of starting a canvas pan — the same hook the hexagons use.
      data-hex="1"
      onPointerDown={onPointerDown}
      onClick={onClick}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick?.();
      }}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      title={name}
      style={{
        position: 'absolute',
        left: x * imageWidth,
        top: y * imageHeight,
        // Scaling about the centre then shifting by half the UNSCALED box lands the pin's
        // centre exactly on the point at any zoom — see the note at the top of the file.
        transform: `translate(-50%, -50%) scale(${1 / scale})`,
        cursor: editable ? (dragging ? 'grabbing' : 'grab') : 'pointer',
        zIndex: selected || alerting ? 20 : 10,
      }}
      className="group flex flex-col items-center"
    >
      <div className="relative" style={{ width: MARKER_SIZE, height: MARKER_SIZE }}>
        {/* Ripple — one expanding ring, drawn behind the pin. */}
        {alerting && !dragging && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              backgroundColor: fx!.core,
              animation: `mapPinRipple ${fx!.duration} ease-out infinite`,
            }}
          />
        )}

        {/* FACING: the coverage cone plus a notch on the rim, both aimed along `rotation`.
            Drawn for every pin — direction is part of the install, and an operator reading
            the plan needs to know which way a camera looks, not just that it exists. The
            cone fades out; the notch stays solid so the bearing is legible even where cones
            from neighbouring cameras overlap. */}
        <svg
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2"
          width={MARKER_SIZE * CONE_REACH * 2}
          height={MARKER_SIZE * CONE_REACH * 2}
          viewBox="0 0 100 100"
          style={{ transform: `translate(-50%, -50%) rotate(${rotation}deg)` }}
        >
          <defs>
            <radialGradient id={`cone-${gradientId}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={fill} stopOpacity={colorState === 'offline' ? 0.3 : 0.5} />
              <stop offset="100%" stopColor={fill} stopOpacity={0} />
            </radialGradient>
          </defs>
          <path d={conePath(fov, 46)} fill={`url(#cone-${gradientId})`} />
          {/* The notch: a small solid arrowhead whose base meets the pin's white ring
              (the pin spans viewBox y 37→63 at this reach, so 39 tucks under the rim). */}
          <path d="M50,26 L57,39 L43,39 Z" fill={fill} stroke="#ffffff" strokeWidth={1.5} strokeLinejoin="round" />
        </svg>

        {/* The pin: white ring, severity/state disc, light core. */}
        <span
          className="absolute inset-0 rounded-full ring-2 ring-white shadow-[0_1px_4px_rgba(0,0,0,0.45)]"
          style={{
            backgroundColor: fill,
            animation: alerting && !dragging ? `mapPinPulse ${fx!.duration} ease-in-out infinite` : undefined,
          }}
        />
        <span
          className="absolute rounded-full"
          style={
            {
              backgroundColor: core,
              width: MARKER_SIZE * 0.3,
              height: MARKER_SIZE * 0.3,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            } as CSSProperties
          }
        />

        {selected && (
          <span className="pointer-events-none absolute -inset-1 rounded-full ring-2 ring-brand" aria-hidden />
        )}

        {/* ROTATION KNOB — drag it around the pin to aim the camera. Direct manipulation on
            the plan is how you match a bearing to a real doorway; the dialog's slider is for
            typing in an exact figure afterwards. Orbits at the current bearing so the knob
            is always the thing the cone points at. */}
        {showRotateHandle && (
          <div
            className="absolute left-1/2 top-1/2"
            style={{
              width: HANDLE_ORBIT * 2,
              height: HANDLE_ORBIT * 2,
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
            }}
          >
            {/* Leader from the pin to the knob, so the knob reads as attached. */}
            <span
              aria-hidden
              className="pointer-events-none absolute left-1/2 bg-brand/60"
              style={{ top: 0, height: HANDLE_ORBIT, width: 1.5, transform: 'translateX(-50%)' }}
            />
            <button
              type="button"
              aria-label={`Rotate ${name}`}
              title="Drag to aim the camera"
              onPointerDown={onRotatePointerDown}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              className={`absolute left-1/2 flex items-center justify-center rounded-full border-2 border-white bg-brand shadow-[0_1px_4px_rgba(0,0,0,0.45)] ${
                rotating ? 'cursor-grabbing scale-110' : 'cursor-grab'
              }`}
              style={{ top: 0, height: 14, width: 14, transform: 'translate(-50%, -50%)' }}
            />
          </div>
        )}

        {total != null && (
          <span
            className="absolute -right-1.5 -top-1.5 min-w-[14px] rounded-full bg-white px-1 text-center text-[9px] font-extrabold leading-[14px] tabular-nums shadow"
            style={{ color: fx ? fx.core : '#2c3e50' }}
          >
            {total}
          </span>
        )}

        {editable && onRemove && (
          <button
            type="button"
            aria-label={`Remove ${name}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute -left-2 -top-2 hidden h-4 w-4 items-center justify-center rounded-full bg-red-600 text-white shadow group-hover:flex"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="mt-0.5 flex items-center gap-1">
        <StatusDot on={live} title={live ? 'Live: running' : 'Live: stopped'} />
        <StatusDot on={recording} title={recording ? 'Recording: running' : 'Recording: stopped'} />
      </div>

      {/* The name would turn a busy plan into a wall of text, so it appears on hover or
          when the pin is selected. */}
      <span
        className={`pointer-events-none mt-0.5 max-w-[110px] truncate rounded bg-black/70 px-1 text-[9px] font-semibold leading-[13px] text-white ${
          selected ? '' : 'hidden group-hover:block'
        }`}
      >
        {name}
      </span>
    </div>
  );
}
